/**
 * Small generic confirm dialog used across the Finance screens for archive /
 * destructive confirmations. Mirrors the FormModal chrome (overlay, footer,
 * buttons) so it sits visually with the rest of the app. Esc / backdrop close.
 *
 * It can also render as an informational dialog (e.g. a 409 conflict the user
 * can only acknowledge) by hiding the confirm button.
 */

import { useEffect } from 'react';
import styles from '../FormModal.module.css';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  destructive?: boolean;
  /** Hide the confirm button entirely (informational / blocked state). */
  hideConfirm?: boolean;
  /** Optional third button, rendered between cancel and confirm — for dialogs
   *  offering two ways forward (e.g. save *or* discard unsaved edits). */
  extraAction?: { label: string; onClick: () => void; destructive?: boolean };
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmDisabled = false,
  destructive = false,
  hideConfirm = false,
  extraAction,
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <div className={styles.title}>{title}</div>
        </div>
        <div className={styles.body}>
          <div className={styles.field}>
            <span className={styles.message}>
              {message}
            </span>
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.footer}>
            <div className={styles.footerLeft} />
            <div className={styles.footerRight}>
              <button type="button" className={styles.btn} onClick={onClose}>
                {hideConfirm ? 'Close' : cancelLabel}
              </button>
              {extraAction && (
                <button
                  type="button"
                  className={`${styles.btn} ${extraAction.destructive ? styles.btnDanger : ''}`}
                  onClick={extraAction.onClick}
                >
                  {extraAction.label}
                </button>
              )}
              {!hideConfirm && (
                <button
                  type="button"
                  className={`${styles.btn} ${destructive ? styles.btnDanger : styles.btnPrimary}`}
                  onClick={onConfirm}
                  disabled={confirmDisabled}
                >
                  {confirmLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
